import { useCallback, useEffect, useState } from 'react';
import { cccdAdmin, type CccdSupplyRequest } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { toastError, toastSuccess } from '@/lib/toast';
import { formatCurrency } from '@alascorporation/utils';
import { LoadState } from "@/components/ui/LoadState"

const cellInputCls =
  'h-[38px] w-full max-w-xs rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

// Solicitudes que un equipo arma desde su dashboard de Academy sobre el
// catálogo de construcción/decoración (cccd-team-supply-catalog) — nunca
// generan un cobro, solo dejan constancia de qué quiere pedir cada equipo
// para que el admin coordine precio final y entrega por WhatsApp.
export default function SupplyRequestsPage() {
  const [requests, setRequests] = useState<CccdSupplyRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [eventId, setEventId] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await cccdAdmin.listSupplyRequests(eventId.trim() || undefined));
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  async function markReviewed(request: CccdSupplyRequest) {
    setUpdatingId(request.id);
    setRequests((prev) => prev.map((r) => (r.id === request.id ? { ...r, status: 'reviewed' } : r)));
    try {
      await cccdAdmin.updateSupplyRequestStatus(request.id, 'reviewed');
      toastSuccess(`Solicitud de "${request.team.name}" marcada como revisada.`);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo actualizar la solicitud.');
      await load();
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Solicitudes de equipos']}
      title="Solicitudes de equipos"
      subtitle="Artículos del catálogo de construcción que cada equipo pidió desde Academy. No hay cobro aquí — coordina precio final, pago y entrega por WhatsApp, como hoy."
      tabs={[]}
      metrics={[]}
    >
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Filtrar por evento (id)</label>
          <input value={eventId} onChange={(e) => setEventId(e.target.value)} placeholder="ID del evento nacional o regional" className={cellInputCls} />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="grid grid-cols-[1.5fr_2fr_1fr_auto] gap-3 border-b border-gray-100 bg-gray-25 px-5 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
          {['Equipo', 'Artículos', 'Estado', ''].map((h, i) => (
            <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h}</span>
          ))}
        </div>

        {loading ? (
          <LoadState />
        ) : requests.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin solicitudes todavía.</p>
        ) : (
          requests.map((request) => (
            <div key={request.id} className="grid grid-cols-[1.5fr_2fr_1fr_auto] items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
              <span className="font-medium text-gray-700 dark:text-gray-200">{request.team.name}</span>
              <ul className="space-y-0.5 text-sm text-gray-600 dark:text-gray-300">
                {request.items.map((line) => (
                  <li key={line.id}>
                    {line.quantity}× {line.catalogItem.name}{' '}
                    <span className="text-xs text-gray-500 dark:text-gray-400">({formatCurrency(line.catalogItem.priceCop)} c/u)</span>
                  </li>
                ))}
              </ul>
              <span
                className={`w-fit rounded-full px-2.5 py-1 text-xs font-medium ${
                  request.status === 'reviewed'
                    ? 'bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400'
                    : 'bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400'
                }`}
              >
                {request.status === 'reviewed' ? 'Revisada' : 'Pendiente'}
              </span>
              <button
                type="button"
                onClick={() => markReviewed(request)}
                disabled={request.status === 'reviewed' || updatingId === request.id}
                className="h-8 whitespace-nowrap rounded-lg border border-gray-300 px-3 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:hover:bg-gray-800"
              >
                Marcar revisada
              </button>
            </div>
          ))
        )}
      </div>
    </ModuleLayout>
  );
}
